import { useAtom, useAtomValue } from "jotai";
import { type ReactNode, useCallback, useEffect } from "react";

import { isChannelListAllowedAtom } from "@/modules/channel-group";
import { useKeyboardNavigation } from "@/modules/keyboard-navigation";

import { ChannelGroupsContainer } from "./channel-groups/channel-groups-container";
import { ChannelList } from "./channel-list";
import { container } from "./channel-list.css";
import {
  actionChannelListClose,
  actionChannelListExpandChannels,
  actionChannelListExpandChannelsAndGroups,
  channelListAtom,
} from "./channel-list.store";
import { channelListSeparator } from "./channel-selector.css";

/**
 * This wraps the ChannelList and the ChannelGroups components. We either show just the ChannelList or both ChannelList and ChannelGroups.
 * We do not want to have the navigator get confused while the groups are open. So we disable navigation on the ChannelList when the groups are open.
 */
export function ChannelSelector({
  "data-testid": dataTestId,
}: {
  ["data-testid"]?: string;
}): ReactNode {
  const [channelList, dispatchChannelList] = useAtom(channelListAtom);
  const showGroups = channelList.expandLevel === "channels-and-groups";

  // TODO: simplify this. It's doing the same thing 3 times.
  useKeyboardNavigation({
    onBack: useCallback(() => {
      dispatchChannelList(
        showGroups
          ? actionChannelListExpandChannels()
          : actionChannelListClose(),
      );
    }, [dispatchChannelList, showGroups]),
    isEnabled: channelList.expandLevel !== "hidden",
  });

  const doClose = useCallback((): boolean => {
    dispatchChannelList(actionChannelListClose());
    return true;
  }, [dispatchChannelList]);

  // Hide itself when we are not allowed to have the channel list open.
  const isAllowed = useAtomValue(isChannelListAllowedAtom);
  useEffect(() => {
    if (!isAllowed) {
      doClose();
    }
  }, [isAllowed, doClose]);

  const doShowMore = useCallback((): boolean => {
    dispatchChannelList(actionChannelListExpandChannelsAndGroups());
    return true;
  }, [dispatchChannelList]);

  // We need to make sure to collapse the channel list when it is unmounted.
  // Else it will automatically pop open when it is remounted which is undesired. The focus will be off.
  useEffect(() => {
    return () => {
      dispatchChannelList(actionChannelListClose());
    };
  }, [dispatchChannelList]);

  if (channelList.expandLevel === "hidden") {
    return null;
  }

  // TODO: We can pass ChannelGroups a prop to close itself. Is that a better API?
  return (
    <div className={container} data-testid={dataTestId}>
      <ChannelList
        shouldFocus={channelList.expandLevel === "channels"}
        doClose={doClose}
        doShowMore={doShowMore}
        data-testid={`${dataTestId}.list`}
      />
      <div
        data-testid={`${dataTestId}.separator`}
        className={channelListSeparator}
      >
        ...
      </div>
      {showGroups && <ChannelGroupsContainer />}
    </div>
  );
}
